// File Location: src/services/documentService.js

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from './firebaseConfig';

// Document categories and types
export const DOCUMENT_CATEGORIES = {
  MEDICAL: 'medical',
  POLICY: 'policy',
  FINANCIAL: 'financial',
  KYC: 'kyc',
  HOSPITAL: 'hospital',
  DISCHARGE: 'discharge',
  PRESCRIPTION: 'prescription',
  BILLS: 'bills',
  ADDITIONAL: 'additional'
};

export const DOCUMENT_TYPES = {
  MEDICAL_DOCUMENT: 'medical_document',
  POLICY_DOCUMENT: 'policy_document',
  PRESCRIPTION: 'prescription',
  BILL: 'bill',
  DISCHARGE_SUMMARY: 'discharge_summary',
  REGISTRATION_CERTIFICATE: 'registration_certificate',
  HOSPITAL_TARIFF: 'hospital_tariff',
  MD_KYC: 'md_kyc',
  ADDITIONAL_DOCUMENT: 'additional_document',
  KYC_DOCUMENT: 'kyc_document'
};

export const DOCUMENT_STATUS = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
  UNDER_REVIEW: 'under_review'
};

// Get all documents with filtering and pagination
export const getAllDocuments = async (filters = {}, pagination = {}) => {
  try {
    const {
      category = 'all',
      status = 'all',
      userType = 'all',
      searchTerm = '',
      sortBy = 'uploadedAt',
      sortOrder = 'desc',
      pageSize = 20,
      lastDoc = null
    } = { ...filters, ...pagination };

    // Build query for patient applications documents
    let applicationsQuery = query(
      collection(db, 'patientApplications'),
      orderBy(sortBy, sortOrder)
    );

    if (pageSize) {
      applicationsQuery = query(applicationsQuery, limit(pageSize));
    }

    if (lastDoc) {
      applicationsQuery = query(applicationsQuery, startAfter(lastDoc));
    }

    const applicationsSnapshot = await getDocs(applicationsQuery);
    const applicationsDocs = applicationsSnapshot.docs;

    // Extract documents from applications
    const documents = [];
    applicationsDocs.forEach(doc => {
      const appData = doc.data();
      
      // Process main documents
      if (appData.documents) {
        Object.entries(appData.documents).forEach(([key, docData]) => {
          if (docData && docData.url) {
            documents.push({
              id: `${doc.id}_${key}`,
              applicationId: doc.id,
              patientId: appData.patient?.id || appData.patientId,
              hospitalId: appData.hospitalId,
              fileName: docData.fileName || key,
              title: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
              category: getDocumentCategory(docData.type || key),
              type: docData.type || 'document',
              size: docData.size || 0,
              uploadedAt: docData.uploadedAt?.toDate() || new Date(),
              uploadedBy: docData.uploadedBy || 'hospital',
              uploaderName: docData.uploaderName || 'Hospital',
              status: docData.status || DOCUMENT_STATUS.PENDING,
              url: docData.url,
              description: docData.description || '',
              source: 'application'
            });
          }
        });
      }

      // Process discharge documents
      if (appData.dischargeDocuments && Array.isArray(appData.dischargeDocuments)) {
        appData.dischargeDocuments.forEach((docData, index) => {
          if (docData && docData.url) {
            documents.push({
              id: `${doc.id}_discharge_${index}`,
              applicationId: doc.id,
              patientId: appData.patient?.id || appData.patientId,
              hospitalId: appData.hospitalId,
              fileName: docData.fileName || docData.title,
              title: docData.title || 'Discharge Document',
              category: DOCUMENT_CATEGORIES.DISCHARGE,
              type: DOCUMENT_TYPES.DISCHARGE_SUMMARY,
              size: docData.size || 0,
              uploadedAt: docData.uploadedAt?.toDate() || new Date(),
              uploadedBy: docData.uploadedBy || 'doctor',
              uploaderName: docData.uploaderName || 'Doctor',
              status: docData.dispatchStatus || DOCUMENT_STATUS.PENDING,
              url: docData.url,
              description: docData.description || '',
              source: 'discharge'
            });
          }
        });
      }

      // Process dispatch documents
      if (appData.dispatchDocuments && Array.isArray(appData.dispatchDocuments)) {
        appData.dispatchDocuments.forEach((docData, index) => {
          if (docData && docData.url) {
            documents.push({
              id: `${doc.id}_dispatch_${index}`,
              applicationId: doc.id,
              patientId: appData.patient?.id || appData.patientId,
              hospitalId: appData.hospitalId,
              fileName: docData.fileName || docData.title,
              title: docData.title || 'Dispatch Document',
              category: DOCUMENT_CATEGORIES.DISCHARGE,
              type: DOCUMENT_TYPES.DISCHARGE_SUMMARY,
              size: docData.size || 0,
              uploadedAt: docData.uploadedAt?.toDate() || new Date(),
              uploadedBy: docData.uploadedBy || 'hospital',
              uploaderName: docData.uploaderName || 'Hospital',
              status: docData.dispatchStatus || DOCUMENT_STATUS.PENDING,
              url: docData.url,
              description: docData.description || '',
              source: 'dispatch'
            });
          }
        });
      }
    });

    // Get hospital registration documents
    const hospitalsQuery = query(
      collection(db, 'hospitalApplications'),
      orderBy('createdAt', 'desc')
    );
    const hospitalsSnapshot = await getDocs(hospitalsQuery);
    
    hospitalsSnapshot.docs.forEach(doc => {
      const hospitalData = doc.data();
      
      // Process hospital documents
      if (hospitalData.documents) {
        Object.entries(hospitalData.documents).forEach(([key, docData]) => {
          if (docData && docData.url) {
            documents.push({
              id: `hospital_${doc.id}_${key}`,
              hospitalId: doc.id,
              fileName: docData.fileName || key,
              title: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
              category: DOCUMENT_CATEGORIES.HOSPITAL,
              type: docData.type || 'hospital_document',
              size: docData.size || 0,
              uploadedAt: docData.uploadedAt?.toDate() || new Date(),
              uploadedBy: 'hospital',
              uploaderName: 'Hospital',
              status: hospitalData.status === 'Approved' ? DOCUMENT_STATUS.APPROVED : DOCUMENT_STATUS.PENDING,
              url: docData.url,
              description: docData.description || '',
              source: 'hospital'
            });
          }
        });
      }
    });

    // Get lead generator documents
    const leadQuery = query(
      collection(db, 'leadGeneratorApplications'),
      orderBy('createdAt', 'desc')
    );
    const leadSnapshot = await getDocs(leadQuery);
    
    leadSnapshot.docs.forEach(doc => {
      const leadData = doc.data();
      
      // Process lead generator documents
      if (leadData.documents) {
        Object.entries(leadData.documents).forEach(([key, docData]) => {
          if (docData && docData.url) {
            documents.push({
              id: `lead_${doc.id}_${key}`,
              leadId: doc.id,
              fileName: docData.fileName || key,
              title: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
              category: DOCUMENT_CATEGORIES.KYC,
              type: docData.type || 'kyc_document',
              size: docData.size || 0,
              uploadedAt: docData.uploadedAt?.toDate() || new Date(),
              uploadedBy: 'lead',
              uploaderName: 'Lead Generator',
              status: leadData.status === 'Approved' ? DOCUMENT_STATUS.APPROVED : DOCUMENT_STATUS.PENDING,
              url: docData.url,
              description: docData.description || '',
              source: 'lead'
            });
          }
        });
      }
    });

    // Get discussion documents
    const discussionQuery = query(
      collection(db, 'discussionDocuments'),
      orderBy('uploadedAt', 'desc')
    );
    const discussionSnapshot = await getDocs(discussionQuery);
    
    discussionSnapshot.docs.forEach(doc => {
      const discussionData = doc.data();
      
      if (discussionData.url) {
        documents.push({
          id: `discussion_${doc.id}`,
          applicationId: discussionData.applicationId,
          discussionId: discussionData.discussionId,
          messageId: discussionData.messageId,
          fileName: discussionData.fileName || discussionData.originalName,
          title: discussionData.description || 'Discussion Document',
          category: DOCUMENT_CATEGORIES.ADDITIONAL,
          type: 'discussion_document',
          size: discussionData.size || 0,
          uploadedAt: discussionData.uploadedAt?.toDate() || new Date(),
          uploadedBy: discussionData.uploadedBy || 'user',
          uploaderName: discussionData.uploaderName || 'User',
          status: DOCUMENT_STATUS.APPROVED,
          url: discussionData.url,
          description: discussionData.description || '',
          source: 'discussion'
        });
      }
    });

    // Apply filters
    let filteredDocuments = documents;

    // Category filter
    if (category !== 'all') {
      filteredDocuments = filteredDocuments.filter(doc => doc.category === category);
    }

    // Status filter
    if (status !== 'all') {
      filteredDocuments = filteredDocuments.filter(doc => doc.status === status);
    }

    // User type filter
    if (userType !== 'all') {
      filteredDocuments = filteredDocuments.filter(doc => doc.uploadedBy === userType);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredDocuments = filteredDocuments.filter(doc =>
        doc.fileName.toLowerCase().includes(searchLower) ||
        doc.title.toLowerCase().includes(searchLower) ||
        doc.description.toLowerCase().includes(searchLower)
      );
    }

    // Sort documents
    filteredDocuments.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'fileName':
          aValue = a.fileName.toLowerCase();
          bValue = b.fileName.toLowerCase();
          break;
        case 'size':
          aValue = a.size;
          bValue = b.size;
          break;
        case 'uploadedBy':
          aValue = a.uploaderName.toLowerCase();
          bValue = b.uploaderName.toLowerCase();
          break;
        case 'category':
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        default: // uploadedAt
          aValue = new Date(a.uploadedAt);
          bValue = new Date(b.uploadedAt);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return {
      success: true,
      documents: filteredDocuments,
      totalCount: filteredDocuments.length,
      hasMore: false // For now, we're not implementing pagination
    };

  } catch (error) {
    console.error('Error fetching documents:', error);
    return {
      success: false,
      error: error.message,
      documents: [],
      totalCount: 0
    };
  }
};

// Get documents by category
export const getDocumentsByCategory = async (category) => {
  try {
    const result = await getAllDocuments({ category });
    return result;
  } catch (error) {
    console.error('Error fetching documents by category:', error);
    return {
      success: false,
      error: error.message,
      documents: []
    };
  }
};

// Get documents by user type
export const getDocumentsByUserType = async (userType) => {
  try {
    const result = await getAllDocuments({ userType });
    return result;
  } catch (error) {
    console.error('Error fetching documents by user type:', error);
    return {
      success: false,
      error: error.message,
      documents: []
    };
  }
};

// Get document statistics
export const getDocumentStatistics = async () => {
  try {
    const result = await getAllDocuments();
    
    if (!result.success) {
      return result;
    }

    const documents = result.documents;
    const stats = {
      total: documents.length,
      byCategory: {},
      byStatus: {},
      byUserType: {},
      recentUploads: 0
    };

    // Count by category
    Object.values(DOCUMENT_CATEGORIES).forEach(category => {
      stats.byCategory[category] = documents.filter(doc => doc.category === category).length;
    });

    // Count by status
    Object.values(DOCUMENT_STATUS).forEach(status => {
      stats.byStatus[status] = documents.filter(doc => doc.status === status).length;
    });

    // Count by user type
    const userTypes = ['hospital', 'doctor', 'policy', 'finance', 'admin', 'lead'];
    userTypes.forEach(userType => {
      stats.byUserType[userType] = documents.filter(doc => doc.uploadedBy === userType).length;
    });

    // Count recent uploads (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    stats.recentUploads = documents.filter(doc => 
      new Date(doc.uploadedAt) > sevenDaysAgo
    ).length;

    return {
      success: true,
      statistics: stats
    };

  } catch (error) {
    console.error('Error fetching document statistics:', error);
    return {
      success: false,
      error: error.message,
      statistics: null
    };
  }
};

// Helper function to determine document category
const getDocumentCategory = (type) => {
  const categoryMap = {
    [DOCUMENT_TYPES.MEDICAL_DOCUMENT]: DOCUMENT_CATEGORIES.MEDICAL,
    [DOCUMENT_TYPES.PRESCRIPTION]: DOCUMENT_CATEGORIES.PRESCRIPTION,
    [DOCUMENT_TYPES.POLICY_DOCUMENT]: DOCUMENT_CATEGORIES.POLICY,
    [DOCUMENT_TYPES.BILL]: DOCUMENT_CATEGORIES.BILLS,
    [DOCUMENT_TYPES.DISCHARGE_SUMMARY]: DOCUMENT_CATEGORIES.DISCHARGE,
    [DOCUMENT_TYPES.REGISTRATION_CERTIFICATE]: DOCUMENT_CATEGORIES.HOSPITAL,
    [DOCUMENT_TYPES.HOSPITAL_TARIFF]: DOCUMENT_CATEGORIES.HOSPITAL,
    [DOCUMENT_TYPES.MD_KYC]: DOCUMENT_CATEGORIES.KYC,
    [DOCUMENT_TYPES.KYC_DOCUMENT]: DOCUMENT_CATEGORIES.KYC,
    [DOCUMENT_TYPES.ADDITIONAL_DOCUMENT]: DOCUMENT_CATEGORIES.ADDITIONAL
  };

  return categoryMap[type] || DOCUMENT_CATEGORIES.ADDITIONAL;
};

// Update document status
export const updateDocumentStatus = async (documentId, status, remarks = '') => {
  try {
    // This would need to be implemented based on the document source
    // For now, we'll return a success response
    console.log(`Updating document ${documentId} status to ${status}`);
    
    return {
      success: true,
      message: 'Document status updated successfully'
    };

  } catch (error) {
    console.error('Error updating document status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete document
export const deleteDocument = async (documentId) => {
  try {
    // This would need to be implemented based on the document source
    // For now, we'll return a success response
    console.log(`Deleting document ${documentId}`);
    
    return {
      success: true,
      message: 'Document deleted successfully'
    };

  } catch (error) {
    console.error('Error deleting document:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get document by ID
export const getDocumentById = async (documentId) => {
  try {
    const result = await getAllDocuments();
    
    if (!result.success) {
      return result;
    }

    const document = result.documents.find(doc => doc.id === documentId);
    
    if (!document) {
      return {
        success: false,
        error: 'Document not found'
      };
    }

    return {
      success: true,
      document
    };

  } catch (error) {
    console.error('Error fetching document by ID:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export default {
  getAllDocuments,
  getDocumentsByCategory,
  getDocumentsByUserType,
  getDocumentStatistics,
  updateDocumentStatus,
  deleteDocument,
  getDocumentById,
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  DOCUMENT_STATUS
};
